// hooks/useDebouncedAutosave.js
import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Debounced autosave.
 *
 *   const { status, schedule, flush } = useDebouncedAutosave(saveFn, 800);
 *   schedule(value)  -> saves `value` after `delay` ms without further changes
 *   flush()          -> saves a pending value immediately (e.g. on blur)
 *
 * - Saves run one after another, so an older request can never finish after
 *   a newer one and overwrite it.
 * - A pending value is saved when the component unmounts (tab switch,
 *   navigating away), so nothing typed gets lost.
 *
 * status: "idle" | "pending" | "saving" | "saved" | "error"
 */
export function useDebouncedAutosave(save, delay = 800) {
  const [status, setStatus] = useState("idle");
  const timerRef = useRef(null);
  const hasPendingRef = useRef(false);
  const pendingValueRef = useRef(undefined);
  const chainRef = useRef(Promise.resolve());
  const saveRef = useRef(save);
  saveRef.current = save;

  const flush = useCallback(() => {
    clearTimeout(timerRef.current);
    timerRef.current = null;
    if (!hasPendingRef.current) return chainRef.current;

    const value = pendingValueRef.current;
    hasPendingRef.current = false;
    pendingValueRef.current = undefined;

    chainRef.current = chainRef.current.then(async () => {
      setStatus("saving");
      try {
        await saveRef.current(value);
        setStatus(hasPendingRef.current ? "pending" : "saved");
      } catch (err) {
        console.error("Autosave failed:", err);
        setStatus("error");
      }
    });
    return chainRef.current;
  }, []);

  const schedule = useCallback(
    (value) => {
      pendingValueRef.current = value;
      hasPendingRef.current = true;
      setStatus("pending");
      clearTimeout(timerRef.current);
      timerRef.current = setTimeout(flush, delay);
    },
    [delay, flush]
  );

  // Save whatever is still pending when the component goes away
  useEffect(() => () => { flush(); }, [flush]);

  return { status, schedule, flush };
}
